/**
 * StateSelector — Persistent State Selector Component (V100)
 * Shown in States workspace header for switching between state contexts.
 */
import React, { useState } from "react";
import { Map, ChevronDown, Search } from "lucide-react";

export default function StateSelector({ states, activeState, onSelect }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");

  const filtered = (states || []).filter(s =>
    !search || s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.geography?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative" data-testid="state-selector">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white transition-colors"
        data-testid="state-selector-trigger">
        <Map size={12} className="text-teal-300 shrink-0" />
        <span className="text-[10px] font-bold max-w-[140px] truncate">
          {activeState?.name || activeState?.geography || "Select State"}
        </span>
        <ChevronDown size={11} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearch(""); }} />
          {/* Dropdown */}
          <div className="absolute top-9 left-0 bg-white border border-slate-200 rounded-xl shadow-xl z-50 w-72"
            data-testid="state-selector-dropdown">
            <div className="p-2 border-b border-slate-100">
              <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-xl">
                <Search size={11} className="text-slate-400" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search states..."
                  className="flex-1 text-xs bg-transparent outline-none text-slate-700"
                  data-testid="state-search-input"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <p className="text-[9px] text-slate-400 px-4 py-3 text-center italic">No states found</p>
              ) : (
                filtered.map(s => (
                  <button key={s.state_id || s.geography}
                    onClick={() => { onSelect(s); setOpen(false); setSearch(""); }}
                    className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 text-left ${
                      activeState?.state_id === s.state_id ? "bg-teal-50" : ""
                    }`}
                    data-testid={`state-option-${s.state_id || s.geography}`}>
                    <div>
                      <p className={`text-xs font-semibold ${activeState?.state_id === s.state_id ? "text-teal-700" : "text-[#0F172A]"}`}>
                        {s.name}
                      </p>
                      <p className="text-[8px] text-slate-400">{s.geography}</p>
                    </div>
                    <div className="text-right">
                      {s.programme_count > 0 && (
                        <span className="text-[7px] bg-teal-50 text-teal-600 px-1.5 py-0.5 rounded-full">
                          {s.programme_count} prog
                        </span>
                      )}
                      {activeState?.state_id === s.state_id && (
                        <span className="text-[7px] text-teal-500 font-bold block mt-0.5">Active</span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
